<script setup lang="ts">
import type { DeletionItem, DeletionPreview } from '@/data/child/deletion';
import { useClipboard } from '@vueuse/core';
import { Check, Clipboard } from 'lucide';
import { computed, ref } from 'vue';
import { SphButton } from '@/components/base/button';
import SphCallout from '@/components/base/callout';
import SphCheckbox from '@/components/base/checkbox';
import SphDialogGeneric from '@/components/base/dialog/internal/DialogGeneric.vue';
import { SphField } from '@/components/base/field';
import SphInput from '@/components/base/input';
import SphLink from '@/components/base/link';
import SphSpinner from '@/components/base/spinner';
import { useI18n } from '@/composables/i18n';
import { useNotifications } from '@/composables/notifications';
import { deleteChild, DELETION_PREVIEW_OUTDATED, fetchDeletionPreview } from '@/data/child/deletion';

defineOptions({
    compatConfig: { MODE: 3 },
});

const props = defineProps<{
    childId: number
    overviewUrl: string
}>();

const { i18n } = useI18n();
const { notify } = useNotifications();

const groupTitles: Record<DeletionItem['group'], string> = {
    fee: i18n.t('Diese Gebühren werden gelöscht:'),
    unlink: i18n.t('Diese Bezugspersonen werden von dem Kind getrennt:'),
    delete: i18n.t('Diese Bezugspersonen werden gelöscht:'),
};

const open = ref(false);
const loading = ref(false);
const preview = ref<DeletionPreview | null>(null);
const outdated = ref(false);
const deleting = ref(false);
const confirmed = ref<Record<string, boolean>>({});
const confirmedName = ref('');

const childName = computed(() => preview.value?.childName ?? '');
const blockers = computed(() => preview.value?.blockers ?? []);
const items = computed(() => preview.value?.items ?? []);

const groups = computed(() => {
    const keys: DeletionItem['group'][] = ['fee', 'unlink', 'delete'];

    return keys
        .map((key) => ({ key, title: groupTitles[key], items: items.value.filter((item) => item.group === key) }))
        .filter((group) => group.items.length > 0);
});

const { copy, copied, isSupported: canCopy } = useClipboard({ source: childName, copiedDuring: 2000 });

const WHITESPACE = /\s+/g;

function itemKey(item: DeletionItem): string {
    return `${item.group}-${item.id}`;
}

function normalize(name: string): string {
    return name.replace(WHITESPACE, ' ').trim().toLocaleLowerCase('de');
}

const canDelete = computed(() => preview.value !== null
    && blockers.value.length === 0
    && items.value.every((item) => confirmed.value[itemKey(item)])
    && normalize(String(confirmedName.value ?? '')) === normalize(childName.value));

async function loadPreview() {
    loading.value = true;
    confirmed.value = {};
    try {
        preview.value = await fetchDeletionPreview(props.childId);
    } catch {
        open.value = false;
        notify.error('Daten konnten nicht geladen werden');
    } finally {
        loading.value = false;
    }
}

function openDialog() {
    outdated.value = false;
    confirmedName.value = '';
    open.value = true;
    void loadPreview();
}

async function submit() {
    if (!canDelete.value || preview.value === null) {
        return;
    }

    deleting.value = true;
    try {
        await deleteChild(props.childId, preview.value.token);
        notify.success('Das Kind wurde gelöscht.', {}, { saveInSession: true });
        window.location.href = props.overviewUrl;
    } catch (error: any) {
        if (error.status !== 409 || error.message !== DELETION_PREVIEW_OUTDATED) {
            notify.error('Daten konnten nicht gelöscht werden');

            return;
        }

        outdated.value = true;
        await loadPreview();
    } finally {
        deleting.value = false;
    }
}
</script>

<template>
    <SphButton
        variant="destructive"
        :label="i18n.t('Datensatz löschen')"
        @click="openDialog"
    />

    <SphDialogGeneric
        v-model:state="open"
        :title="i18n.t('Kind löschen')"
        class="sm:w-xl!"
    >
        <SphSpinner
            v-if="loading"
            variant="centered"
            class="min-h-32"
        />
        <form
            v-else
            id="child-delete-form"
            class="flex flex-col gap-6"
            @submit.prevent="submit"
        >
            <SphCallout
                v-if="outdated"
                type="warning"
                :title="i18n.t('Die Daten des Kindes haben sich inzwischen geändert. Bitte prüfen Sie die Angaben erneut.')"
            />

            <SphCallout
                v-if="blockers.length > 0"
                type="error"
                :title="i18n.t('Das Kind kann nicht gelöscht werden, weil:')"
            >
                <template #description>
                    <ul class="list-disc pl-5">
                        <li
                            v-for="(blocker, index) in blockers"
                            :key="index"
                        >
                            {{ blocker.text }}
                            <template v-if="blocker.subject">
                                <SphLink
                                    v-if="blocker.url"
                                    :href="blocker.url"
                                    :label="blocker.subject"
                                />
                                <strong v-else>{{ blocker.subject }}</strong>
                            </template>
                            <SphLink
                                v-else-if="blocker.url"
                                :href="blocker.url"
                                :label="i18n.t('Anzeigen')"
                            />
                        </li>
                    </ul>
                </template>
            </SphCallout>

            <template v-else>
                <p>
                    {{ i18n.t('Das Kind wird endgültig gelöscht. Dies kann nicht rückgängig gemacht werden.') }}
                    <template v-if="items.length > 0">
                        {{ i18n.t('Bitte bestätigen Sie jeden Eintrag, der dabei mitgelöscht oder getrennt wird.') }}
                    </template>
                </p>

                <section
                    v-for="group in groups"
                    :key="group.key"
                    class="flex flex-col gap-2"
                >
                    <h3 class="kgr-headline kgr-headline--h4">
                        {{ group.title }}
                    </h3>
                    <div
                        v-for="item in group.items"
                        :key="itemKey(item)"
                        :data-item="itemKey(item)"
                    >
                        <SphField
                            v-slot="field"
                            :label="item.label"
                            :description="item.detail"
                            inline
                            compact
                            required
                        >
                            <SphCheckbox
                                v-bind="field"
                                v-model="confirmed[itemKey(item)]"
                            />
                        </SphField>
                    </div>
                </section>

                <SphField
                    v-slot="field"
                    :label="i18n.t('Zur Bestätigung den Namen des Kindes eingeben:')"
                    required
                >
                    <div class="flex items-center gap-2">
                        <span class="rounded-md bg-muted px-2 py-0.5 font-semibold">{{ childName }}</span>
                        <SphButton
                            v-if="canCopy"
                            data-copy-name
                            variant="transparent"
                            size="sm"
                            :icon="copied ? Check : Clipboard"
                            :title="i18n.t('Ins Clipboard kopieren')"
                            :aria-label="i18n.t('Ins Clipboard kopieren')"
                            @click="copy()"
                        />
                    </div>
                    <SphInput
                        v-bind="field"
                        v-model="confirmedName"
                        class="mt-2 w-full"
                        autocomplete="off"
                    />
                </SphField>
            </template>
        </form>

        <template #footer="{ dialog }">
            <SphButton
                :label="i18n.t('Abbrechen')"
                @click="dialog.close()"
            />
            <SphButton
                v-if="!loading && blockers.length === 0"
                type="submit"
                form="child-delete-form"
                variant="destructive"
                :label="i18n.t('Datensatz löschen')"
                :disabled="!canDelete"
                :loading="deleting"
            />
        </template>
    </SphDialogGeneric>
</template>
